import { DICTATION_REPORT_STATUS } from "@rosc/contracts";
import { StatusFile } from "@/common/enums/enums";
import { builtInTemplateLabel } from "@/lib/document-name";
import { isPreChartDraft, PRE_CHART_STATUS } from "@/lib/pre-chart";
import { getVisitTitle } from "@/lib/utils";
import type { VisitSummary } from "@/services/visits/read-model";

// What the list shows for a visit's server status.
export type VisitListStatus =
  | "processing"
  | "generating"
  | "ready"
  | "error"
  // A pre-chart draft: prepared, not recorded yet.
  | "preChart"
  // A dictated report (radiology dictation), saved as a visit.
  | "report"
  | "none";

export const visitListStatus = (status: string | null): VisitListStatus => {
  switch (status) {
    case StatusFile.PROCESSING.toString():
      return "processing";
    case StatusFile.GENERATING.toString():
      return "generating";
    case StatusFile.READY.toString():
      return "ready";
    case StatusFile.ERROR.toString():
      return "error";
    case PRE_CHART_STATUS:
      return "preChart";
    case DICTATION_REPORT_STATUS:
      return "report";
    default:
      return "none";
  }
};

export type VisitStatusFilter =
  | "all"
  | "processing"
  | "ready"
  | "error"
  | "needsReview";
// The status pills. "Needs review" is its own chip (see VisitsHome).
export const VISIT_STATUS_FILTERS: readonly VisitStatusFilter[] = [
  "all",
  "processing",
  "ready",
  "error",
];

// Values in the visit's note still marked "[verify]" (not confirmed by the
// clinician). Undefined when the list was read without the count.
export const needsReviewCount = (visit: VisitSummary): number =>
  visit.needsReviewCount ?? 0;
export const needsReview = (visit: VisitSummary): boolean =>
  needsReviewCount(visit) > 0;
// Whether the list carries review counts at all (the API sends them only on
// request), so the filter is not offered without them.
export const hasReviewCounts = (visits: readonly VisitSummary[]): boolean =>
  visits.some((visit) => visit.needsReviewCount !== undefined);

export const matchesStatusFilter = (
  status: string | null,
  filter: Exclude<VisitStatusFilter, "needsReview">,
): boolean => {
  if (filter === "all") return true;
  const value = visitListStatus(status);
  // "Processing" covers every visit the server is still working on.
  if (filter === "processing")
    return value === "processing" || value === "generating";
  return value === filter;
};

// Case- and accent-insensitive, so "cafe" finds "Café".
export const normalizeSearch = (value: string): string =>
  value
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();

export const matchesSearch = (visit: VisitSummary, query: string): boolean => {
  const needle = normalizeSearch(query);
  if (!needle) return true;
  const haystack = normalizeSearch(
    [
      getVisitTitle(visit.title, visit.rowno),
      visit.template ?? "",
      visit.template ? builtInTemplateLabel(visit.template) : "",
    ].join(" "),
  );
  return needle.split(/\s+/).every((word) => haystack.includes(word));
};

export const matchesFilter = (
  visit: VisitSummary,
  filter: VisitStatusFilter,
): boolean =>
  filter === "needsReview"
    ? needsReview(visit)
    : matchesStatusFilter(visit.status, filter);

export const filterVisits = (
  visits: readonly VisitSummary[],
  query: string,
  filter: VisitStatusFilter,
): VisitSummary[] =>
  visits.filter(
    (visit) => matchesFilter(visit, filter) && matchesSearch(visit, query),
  );

// "needsReview" counts visits (not values) with something to review.
export const countByFilter = (
  visits: readonly VisitSummary[],
): Record<VisitStatusFilter, number> => {
  const counts = {
    all: visits.length,
    processing: 0,
    ready: 0,
    error: 0,
    needsReview: 0,
  };
  for (const visit of visits)
    for (const filter of [
      "processing",
      "ready",
      "error",
      "needsReview",
    ] as const)
      if (matchesFilter(visit, filter)) counts[filter] += 1;
  return counts;
};

// The API sends "2026-10-15 14:40:00" (UTC, no zone); tests and stories may
// send ISO strings with a zone.
export const parseVisitDate = (value: string | null): Date | null => {
  if (!value) return null;
  let iso = value.trim().replace(" ", "T");
  if (!/(Z|[+-]\d{2}:?\d{2})$/i.test(iso)) iso += "Z";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

export type VisitGroupKey =
  // Pre-chart drafts (not started yet), pinned above the dates.
  | { kind: "preChart" }
  | { kind: "today" }
  | { kind: "yesterday" }
  | { kind: "lastWeek" }
  | { kind: "month"; year: number; month: number }
  | { kind: "undated" };

export interface VisitGroup {
  id: string;
  key: VisitGroupKey;
  visits: VisitSummary[];
}

const startOfDay = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const DAY = 24 * 60 * 60 * 1000;

export const groupKeyFor = (date: Date | null, now: Date): VisitGroupKey => {
  if (!date) return { kind: "undated" };
  const days = Math.round((startOfDay(now) - startOfDay(date)) / DAY);
  if (days <= 0) return { kind: "today" };
  if (days === 1) return { kind: "yesterday" };
  if (days < 7) return { kind: "lastWeek" };
  return { kind: "month", year: date.getFullYear(), month: date.getMonth() };
};

// The date bucket a row's time or date is written for; a pinned pre-chart
// row uses its own date's bucket.
export const dateKindFor = (
  visit: VisitSummary,
  group: VisitGroupKey["kind"],
  now: Date = new Date(),
): Exclude<VisitGroupKey["kind"], "preChart"> => {
  if (group !== "preChart") return group;
  const kind = groupKeyFor(parseVisitDate(visit.createDate), now).kind;
  // Not under a "Yesterday" label: written with its day, not only a time.
  if (kind === "yesterday") return "lastWeek";
  return kind as Exclude<VisitGroupKey["kind"], "preChart">;
};

const groupId = (key: VisitGroupKey): string =>
  key.kind === "month" ? `month-${key.year}-${key.month}` : key.kind;

// The pinned pre-chart section shows this many drafts (the newest) until
// "Show all" is pressed, so many drafts do not take over the list.
export const PRE_CHART_SECTION_LIMIT = 5;

const createdAt = (visit: VisitSummary): number =>
  parseVisitDate(visit.createDate)?.getTime() ?? 0;

// Keeps the list order (newest first) and starts a new group whenever the
// date bucket changes. With `pinPreChart` (clinician request 05/11), the
// pre-chart drafts come first in their own "preChart" group, newest first,
// and the date groups hold only the other visits; no group when there are
// none.
export const groupVisits = (
  visits: readonly VisitSummary[],
  now: Date = new Date(),
  { pinPreChart = false }: { pinPreChart?: boolean } = {},
): VisitGroup[] => {
  const groups: VisitGroup[] = [];
  const byId = new Map<string, VisitGroup>();
  let dated: readonly VisitSummary[] = visits;
  if (pinPreChart) {
    const drafts = visits.filter((visit) => isPreChartDraft(visit.status));
    if (drafts.length) {
      groups.push({
        id: "preChart",
        key: { kind: "preChart" },
        visits: [...drafts].sort((a, b) => createdAt(b) - createdAt(a)),
      });
      dated = visits.filter((visit) => !isPreChartDraft(visit.status));
    }
  }
  for (const visit of dated) {
    const key = groupKeyFor(parseVisitDate(visit.createDate), now);
    const id = groupId(key);
    let group = byId.get(id);
    if (!group) {
      group = { id, key, visits: [] };
      byId.set(id, group);
      groups.push(group);
    }
    group.visits.push(visit);
  }
  return groups;
};

// "12:04" under an hour, "1:02:03" from an hour.
export const formatVisitDuration = (seconds: number | null): string | null => {
  if (seconds === null || !Number.isFinite(seconds) || seconds <= 0)
    return null;
  const total = Math.round(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = String(total % 60).padStart(2, "0");
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${secs}`
    : `${minutes}:${secs}`;
};
